import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | expired | error
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (res.data.payment_status === 'expired' || res.data.status === 'expired') { setState('expired'); return; }
        if (attemptsRef.current >= MAX_ATTEMPTS) { setState('pending'); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setState('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <Card className="shadow-[var(--shadow-md)] text-center max-w-lg w-full">
        <CardContent className="py-12">
          {state === 'checking' && (
            <div data-testid="payment-checking">
              <div className="w-16 h-16 bg-[#238A92] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Clock size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Verifica del pagamento...</h2>
              <p className="text-muted-foreground">Attendi qualche secondo, stiamo confermando il pagamento con Stripe.</p>
            </div>
          )}
          {state === 'paid' && (
            <div data-testid="payment-success">
              <div className="w-16 h-16 bg-[#1E7A58] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Pagamento Completato!</h2>
              <p className="text-muted-foreground mb-6">Grazie! Il pagamento è stato registrato e collegato al tuo appuntamento.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={() => navigate('/i-miei-appuntamenti')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="payment-success-appointments-btn">I Miei Appuntamenti</Button>
                <Button variant="outline" onClick={() => navigate('/')}>Torna alla Home</Button>
              </div>
            </div>
          )}
          {state === 'pending' && (
            <div data-testid="payment-pending">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>Pagamento in elaborazione</h2>
              <p className="text-muted-foreground mb-6">Il pagamento è in corso di conferma. Controlla tra qualche minuto lo stato in "I Miei Appuntamenti".</p>
              <Button onClick={() => navigate('/i-miei-appuntamenti')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">I Miei Appuntamenti</Button>
            </div>
          )}
          {(state === 'expired' || state === 'error') && (
            <div data-testid="payment-error">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>{state === 'expired' ? 'Sessione di pagamento scaduta' : 'Verifica non riuscita'}</h2>
              <p className="text-muted-foreground mb-6">Nessun addebito effettuato. Puoi riprovare il pagamento da "I Miei Appuntamenti".</p>
              <Button onClick={() => navigate('/i-miei-appuntamenti')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">I Miei Appuntamenti</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
